import { Observable, Subject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { GlobalSearchViewGroup, GlobalSearchViewResult } from '../global-search.utils';

interface GlobalSearchControllerOptions {
    navigate: (route: string) => void;
    resolve: (query: string) => Observable<GlobalSearchViewGroup[]>;
}

export class GlobalSearchController {
    term = '';
    groups: GlobalSearchViewGroup[] = [];
    results: GlobalSearchViewResult[] = [];
    open = false;
    loading = false;
    highlightedIndex = 0;

    private readonly query$ = new Subject<string>();
    private readonly subscription: Subscription;

    constructor(private readonly options: GlobalSearchControllerOptions) {
        this.subscription = this.query$.pipe(
            map(query => query.trim()),
            debounceTime(200),
            distinctUntilChanged(),
            switchMap(query => this.options.resolve(query))
        ).subscribe(groups => this.applyGroups(groups));
    }

    destroy(): void {
        this.subscription.unsubscribe();
    }

    onChange(searchTerm: string): void {
        this.open = true;
        this.term = searchTerm;
        this.loading = !!searchTerm.trim();
        this.query$.next(searchTerm);
    }

    openSearch(): void {
        this.open = true;
        this.loading = !!this.term.trim();
        this.query$.next(this.term);
    }

    blur(): void {
        window.setTimeout(() => {
            this.open = false;
        }, 120);
    }

    keydown(event: KeyboardEvent): void {
        if (!this.results.length) {
            if (event.key === 'Escape') {
                this.open = false;
            }
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.open = true;
            this.highlightedIndex = (this.highlightedIndex + 1) % this.results.length;
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.open = true;
            this.highlightedIndex = (this.highlightedIndex - 1 + this.results.length) % this.results.length;
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            const selected = this.results[this.highlightedIndex];
            if (selected) {
                this.selectResult(selected);
            }
            return;
        }

        if (event.key === 'Escape') {
            this.open = false;
        }
    }

    selectResult(result: GlobalSearchViewResult): void {
        this.open = false;
        this.clear(false);
        this.options.navigate(result.route);
    }

    clear(keepOpen: boolean = true): void {
        this.term = '';
        this.loading = false;
        this.applyGroups([]);
        this.open = keepOpen;
    }

    private applyGroups(groups: GlobalSearchViewGroup[]): void {
        this.loading = false;
        this.groups = groups;
        this.results = groups.flatMap(group => group.results);
        this.highlightedIndex = Math.min(this.highlightedIndex, Math.max(this.results.length - 1, 0));
    }
}
